import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { BrandLogo } from 'src/components/Header/BrandLogo';
import { CSS_ANIMATION_DURATION, FULL_SCREEN_LOADER_TRANSPARENT_ID } from 'src/config';
import { BrandingModelSelectorType } from 'src/models/Branding/BrandingModel';
import { ROUTE_STOP_PAGE } from 'src/routes';
import { commonSelector, SidebarSelector } from 'src/store/commonPart/selector';
import { overlaySelector } from 'src/store/overlay/selectors';
import { currentUserSelector } from 'src/store/user/selectors';
import { useHover, UseHoverOptionsType } from 'src/utils/hooks/useHover';
import { useRefClickOutside } from 'src/utils/hooks/useRefClickOutside';
import { useTHelper } from 'src/utils/i18n';
import { CampaignOptions } from './components/CampaignOptions';
import { Divider } from './components/common/Divider';
import { GroupExtended } from './components/common/GroupExtended';
import { ItemLink } from './components/common/Item';
import { Header } from './components/Header';
import { SubscriptionButton } from './components/SubscriptionButton';
import { SwitchOrganization } from './components/SwitchOrganization';
import { ToggleButton } from './components/ToggleButton';
import { calculateSelectedItem } from './helpers/calculateSelectedItem';
import { SidebarContext, SidebarContextType } from './helpers/context';
import { showLeftMenuOrNot } from './helpers/showLeftMenuOrNot';
import { useCheckDisabledItems, useStaticMenuItems, useToggleClasses } from './hooks';
import {
  IconAdministration,
  IconCampaignOptions,
  IconGlobalNavigation,
  IconOrganizationSettings,
  IconStartGuide,
} from './icons';
import styles from './styles.module.scss';

type Props = {
  brandingModel: BrandingModelSelectorType;
  authenticated: boolean;

  hideRallyUpLogo?: boolean;
};

export const SideBar: FC<Props> = ({ brandingModel, authenticated, hideRallyUpLogo }) => {
  const tHelper = useTHelper('leftMenu');

  const sidebarModel = useSelector(SidebarSelector);

  const {
    global,
    settings,
    startGuideItem,
    administrationItem,
    partnerAdministrationItem,
    pledgeFundingItem,
    switchOrganization,
  } = useStaticMenuItems(sidebarModel?.MenuItems);

  const disabledItems = useCheckDisabledItems();

  const isStopPage = useRouteMatch(ROUTE_STOP_PAGE);

  const { pathname } = useLocation();

  const { innerWidth, isCurrentPageSPA } = useSelector(commonSelector);

  const { show: overlayOpen } = useSelector(overlaySelector);

  const [showMenu, setShowMenu] = useState(false);
  const [isOpenedVersion, setIsOpenedVersion] = useState(false);
  const [renderMenu, setRender] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isCollapsedVersion = !isOpenedVersion;

  const controlMouseLeave = useCallback<NonNullable<UseHoverOptionsType['controlMouseLeave']>>(
    /** prevent unhovering while displaying the layout fallback element */
    (event) => (event.relatedTarget as HTMLDivElement)?.id === FULL_SCREEN_LOADER_TRANSPARENT_ID,
    []
  );

  const [hoverRef, hovering] = useHover(isCollapsedVersion, { controlMouseLeave });

  const timerRef = useRef<number | null>(null);

  const listenClick = isOpenedVersion;

  const btnMenuHidden = sidebarModel ? sidebarModel.MenuItems.length === 0 || !listenClick : true;

  const collapsedItems = isCollapsedVersion ? !isHovered : false;

  const { changeInitOpen } = useToggleClasses({
    showSidebar: authenticated && renderMenu && !!sidebarModel?.MenuItems.length,
    innerWidth,
    showMenu,
    hasMenuData: !!sidebarModel,
  });

  const close = useCallback(() => {
    setShowMenu(false);
  }, []);

  const ref = useRefClickOutside<HTMLDivElement>(listenClick && renderMenu, close);

  /** decide to show menu or not depending on pathname or screen dimensions */
  useEffect(() => {
    const isCollapsedMenu = showLeftMenuOrNot(pathname, innerWidth, overlayOpen);

    /** fix the flash when the overlay opened */
    if (overlayOpen) {
      setRender(false);
    }

    changeInitOpen(isCollapsedMenu);
    setIsOpenedVersion(!isCollapsedMenu);
    setShowMenu(isCollapsedMenu);
  }, [innerWidth, pathname, overlayOpen, changeInitOpen]);

  const toggleMenu = useCallback(() => {
    setShowMenu((prev) => !prev);
  }, []);

  /** set an unmount flag when menu is close */
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (showMenu) {
      setRender(true);
    } else {
      timerRef.current = window.setTimeout(() => {
        setRender(false);
      }, CSS_ANIMATION_DURATION);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [showMenu]);

  const timerHoverRef = useRef<number | null>(null);

  /** smoothing collapsing the menu  */
  useEffect(() => {
    if (timerHoverRef.current) {
      clearTimeout(timerHoverRef.current);
    }

    if (hovering) {
      setIsHovered(true);
    } else {
      timerHoverRef.current = window.setTimeout(() => {
        setIsHovered(false);
      }, 150);
    }

    return () => {
      if (timerHoverRef.current) {
        clearTimeout(timerHoverRef.current);
      }
    };
  }, [hovering]);

  /** selected link */
  const userModel = useSelector(currentUserSelector);

  const organizationId = userModel.response?.organizationInfo.OrganizationUrl || '';

  const preparedMenuItems = useMemo(
    () => sidebarModel?.MenuItems.map((item) => ({ ...item, Url: item.Url.replace(':id', organizationId) })),
    [organizationId, sidebarModel?.MenuItems]
  );

  const selectedItemId = useMemo(
    () => (preparedMenuItems ? calculateSelectedItem(pathname, preparedMenuItems) : null),
    [pathname, preparedMenuItems]
  );
  /** end selected link */

  /** organization list */
  const [organizationsListIsShowed, setOrganizationsListIsShowed] = useState(false);

  const showOrganizationsList = useCallback(() => {
    setOrganizationsListIsShowed(true);
  }, []);

  const hideOrganizationsList = useCallback(() => {
    setOrganizationsListIsShowed(false);
  }, []);

  useEffect(() => {
    if (!renderMenu || !showMenu) {
      setOrganizationsListIsShowed(false);
    }
  }, [renderMenu, showMenu]);
  /** end organization list */

  const contextValue = useMemo<SidebarContextType>(
    () => ({
      closeMenu: () => {
        if (isOpenedVersion) {
          close();
        }
      },
    }),
    [close, isOpenedVersion]
  );

  if (isStopPage) {
    return (
      <div className={styles.toggleContainer}>
        <BrandLogo brandingModel={brandingModel} hideRallyUpLogo={hideRallyUpLogo} />
      </div>
    );
  }

  return (
    <SidebarContext.Provider value={contextValue}>
      <div className={styles.toggleContainer}>
        {!isCollapsedVersion && <ToggleButton isHidden={btnMenuHidden} showMenu={showMenu} onToggleMenu={toggleMenu} />}

        <BrandLogo brandingModel={brandingModel} hideRallyUpLogo={hideRallyUpLogo} />
      </div>

      {renderMenu && authenticated && !!sidebarModel?.MenuItems.length && (
        <>
          <div className={styles.backdrop} />

          <div
            className={classNames('sidebar_custom', styles.sidebar, {
              [styles.isOpened]: isOpenedVersion && showMenu,
              [styles.isCollapsed]: isCollapsedVersion,
              [styles.isHovered]: isHovered,
              [styles.hide]: organizationsListIsShowed,
            })}
            ref={(element) => {
              if (element) {
                ref.current = element;
              }
              hoverRef(element);
            }}
          >
            <Header onClose={toggleMenu} brandingModel={brandingModel} hideRallyUpLogo={hideRallyUpLogo} />

            <div className={styles.content}>
              {startGuideItem && (
                <ItemLink
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...startGuideItem}
                  icon={IconStartGuide}
                  selected={startGuideItem.id === selectedItemId}
                  isCurrentPageSPA={isCurrentPageSPA}
                />
              )}

              {!!global.length && (
                <>
                  <GroupExtended
                    title={tHelper('section.global')}
                    icon={IconGlobalNavigation}
                    defaultOpened
                    collapsed={collapsedItems}
                    testId="globalNavigation"
                  >
                    {global.map(({ id, title, url, readyAsSPA, openNewTab, testId }) => (
                      <ItemLink
                        key={id}
                        title={title}
                        url={url}
                        readyAsSPA={readyAsSPA}
                        openNewTab={openNewTab}
                        testId={testId}
                        subItem
                        disabled={disabledItems}
                        isCurrentPageSPA={isCurrentPageSPA}
                        selected={id === selectedItemId}
                      />
                    ))}
                  </GroupExtended>

                  <Divider />
                </>
              )}

              {sidebarModel && (
                <>
                  <GroupExtended
                    title={tHelper('section.options')}
                    icon={IconCampaignOptions}
                    defaultOpened
                    collapsed={collapsedItems}
                    testId="campaignOptionsLeftMenu"
                  >
                    <CampaignOptions
                      hasOnlyDraftCampaigns={sidebarModel.HasOnlyDraftCampaigns}
                      selectedCampaignMenuItems={sidebarModel.SelectedCampaignMenuItems}
                      organizerCampaigns={sidebarModel.OrganizerCampaigns}
                      selectedCampaignId={sidebarModel.SelectedCampaignID}
                      canCreateCampaign={sidebarModel.CanCreateCampaign}
                      selectedItemId={selectedItemId}
                      disabledItems={disabledItems}
                      isCurrentPageSPA={isCurrentPageSPA}
                    />
                  </GroupExtended>

                  <Divider />
                </>
              )}

              {!!settings.length && (
                <>
                  <GroupExtended
                    title={tHelper('section.settings')}
                    icon={IconOrganizationSettings}
                    collapsed={collapsedItems}
                  >
                    {settings.map(({ id, title, url, readyAsSPA, openNewTab, testId }) => (
                      <ItemLink
                        key={id}
                        title={title}
                        url={url}
                        readyAsSPA={readyAsSPA}
                        openNewTab={openNewTab}
                        testId={testId}
                        subItem
                        disabled={disabledItems}
                        isCurrentPageSPA={isCurrentPageSPA}
                        selected={id === selectedItemId}
                      />
                    ))}
                  </GroupExtended>

                  <Divider />
                </>
              )}

              <span className={styles.spacer} />

              {administrationItem && (
                <ItemLink
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...administrationItem}
                  icon={IconAdministration}
                  isCurrentPageSPA={isCurrentPageSPA}
                  selected={administrationItem.id === selectedItemId}
                />
              )}

              {partnerAdministrationItem && (
                <ItemLink
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...partnerAdministrationItem}
                  icon={IconAdministration}
                  isCurrentPageSPA={isCurrentPageSPA}
                  selected={partnerAdministrationItem.id === selectedItemId}
                />
              )}

              {pledgeFundingItem && (
                <ItemLink
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...pledgeFundingItem}
                  icon={IconAdministration}
                  isCurrentPageSPA={isCurrentPageSPA}
                  selected={pledgeFundingItem.id === selectedItemId}
                />
              )}

              {switchOrganization && !!sidebarModel?.OrganizerOrganizations.length && (
                <SwitchOrganization
                  title={switchOrganization.title}
                  testId={switchOrganization.testId}
                  onOpen={showOrganizationsList}
                  onClose={hideOrganizationsList}
                  organizations={sidebarModel.OrganizerOrganizations}
                  show={organizationsListIsShowed}
                />
              )}

              {sidebarModel?.DisplayPlusSubscriptionButton && (
                <SubscriptionButton url={sidebarModel.PlusSubscriptionButtonRedirectUrl} />
              )}
            </div>
          </div>
        </>
      )}
    </SidebarContext.Provider>
  );
};
